/**
 * reducers/paths.js
 * desc: workflow paths for Journeys module
 * note: includes Journey Path, Junction Path, and Fallback Path
 */
'use strict';

import { capitalizeEachWord, getObjectFromArray } from '../../utilities';
import { translate } from '../../utilities/localization';
import journeysConstants from '../../modules/JourneysDetail/constants';
import { uniqBy } from 'lodash';
import { createStepsGraph } from '../../modules/JourneysDetail/utilities/steps-graph';
import { buildDataExpression } from '../../components/FxEditor/utilities/expressions';

const INITIAL_STATE = {
    isLoading: {
        copyPath: false,
        createPath: false,
        createPathPublish: false,
        createPathVersionCopy: false,
        createPathVersionRevert: false,
        createStep: false,
        deletePath: false,
        deleteStep: false,
        exportPath: false,
        promotePath: false,
        importPath: false,
        getPath: false,
        getPathUsage: false,
        getPaths: false,
        getPathsJourney: false,
        getPathsJunction: false,
        getPathsFallback: false,
        getPathVersions: false,
        getPathVersionSteps: false,
        getStep: false,
        getStepInputs: false,
        getSteps: false,
        updatePath: false,
        updateStep: false,
        updateStepVersion: false,
    },
    detail: {
        flyoutMeta: {},
        path: {},
        pathInputs: [],
        sequence: [],
        steps: {},
        stepSelection: {},
    },
    list: [],
    usage: []
};


function pathsReducer(state=INITIAL_STATE, action) {
    switch (action.type) {
        // Global UI State 
        case 'SET_FLYOUT_DATA':
            return _setFlyoutData(state, action.payload, action.isOverride);

        // Clear Path Data
        case 'CLEAR_PATH':
            return _clearPath(state);
        case 'CLEAR_STEP_INPUTS':
            return _clearStepInputs(state);

        // COPY Path (Instance)
        case 'COPY_PATH_ERROR':
            return _copyPathError(state);
        case 'COPY_PATH_REQUEST':
            return _copyPathRequest(state);
        case 'COPY_PATH_SUCCESS':
            return _copyPathSuccess(state, action.payload);

        // CREATE Path (Instance)
        case 'CREATE_PATH_ERROR':
            return _createPathError(state);
        case 'CREATE_PATH_REQUEST':
            return _createPathRequest(state);
        case 'CREATE_PATH_SUCCESS':
            return _createPathSuccess(state, action.payload);

        // CREATE Path Publish (Instance)
        case 'CREATE_PATH_PUBLISH_ERROR':
            return _createPathPublishError(state);
        case 'CREATE_PATH_PUBLISH_REQUEST':
            return _createPathPublishRequest(state);
        case 'CREATE_PATH_PUBLISH_SUCCESS':
            return _createPathPublishSuccess(state, action.payload);

        // CREATE Path Version Copy (Instance)
        case 'CREATE_PATH_VERSION_COPY_ERROR':
            return _createPathVersionCopyError(state);
        case 'CREATE_PATH_VERSION_COPY_REQUEST':
            return _createPathVersionCopyRequest(state);
        case 'CREATE_PATH_VERSION_COPY_SUCCESS':
            return _createPathVersionCopySuccess(state);

        // CREATE Path Version Revert (Instance)
        case 'CREATE_PATH_VERSION_REVERT_ERROR':
            return _createPathVersionRevertError(state);
        case 'CREATE_PATH_VERSION_REVERT_REQUEST':
            return _createPathVersionRevertRequest(state);
        case 'CREATE_PATH_VERSION_REVERT_SUCCESS':
            return _createPathVersionRevertSuccess(state);

        // CREATE Step (Instance)
        case 'CREATE_STEP_ERROR':
            return _createStepError(state);
        case 'CREATE_STEP_REQUEST':
            return _createStepRequest(state);
        case 'CREATE_STEP_SUCCESS':
            return _createStepSuccess(state, action.payload);

        // CREATE Virtual Step (Instance)
        case 'CREATE_VIRTUAL_STEP':
            return _createVirtualStep(state, action.payload);

        // DELETE Path (Instance)
        case 'DELETE_PATH_ERROR':
            return _deletePathError(state);
        case 'DELETE_PATH_REQUEST':
            return _deletePathRequest(state);
        case 'DELETE_PATH_SUCCESS':
            return _deletePathSuccess(state, action.payload);

        // DELETE Step (Instance)
        case 'DELETE_STEP_ERROR':
            return _deleteStepError(state);
        case 'DELETE_STEP_REQUEST':
            return _deleteStepRequest(state);
        case 'DELETE_STEP_SUCCESS':
            return _deleteStepSuccess(state, action.payload);

        // DELETE Virtual Step
        case 'DELETE_VIRTUAL_STEP':
            return _deleteVirtualStep(state);

        // Export Path (Instance)
        case 'EXPORT_PATH_ERROR':
            return _exportPathError(state);
        case 'EXPORT_PATH_REQUEST':
            return _exportPathRequest(state);
        case 'EXPORT_PATH_SUCCESS':
            return _exportPathSuccess(state);
        case 'PROMOTE_PATH_ERROR':
            return _promotePathSettled(state);
        case 'PROMOTE_PATH_REQUEST':
            return _promotePathRequest(state);
        case 'PROMOTE_PATH_SUCCESS':
            return _promotePathSettled(state);

        // Import Path (Instance)
        case 'IMPORT_PATH_ERROR':
            return _importPathError(state);
        case 'IMPORT_PATH_REQUEST':
            return _importPathRequest(state);
        case 'IMPORT_PATH_SUCCESS':
            return _importPathSuccess(state);

        // GET Path (Instance)
        case 'GET_PATH_ERROR':
            return _getPathError(state);
        case 'GET_PATH_REQUEST':
            return _getPathRequest(state);
        case 'GET_PATH_SUCCESS':
            return _getPathSuccess(state, action);

        // GET Path Usage (Instance)
        case 'GET_PATH_USAGE_ERROR':
            return _getPathUsageError(state);
        case 'GET_PATH_USAGE_REQUEST':
            return _getPathUsageRequest(state);
        case 'GET_PATH_USAGE_SUCCESS':
            return _getPathUsageSuccess(state, action);

        // GET Path Versions (Collection)
        case 'GET_PATH_VERSIONS_ERROR':
            return _getPathVersionsError(state);
        case 'GET_PATH_VERSIONS_REQUEST':
            return _getPathVersionsRequest(state);
        case 'GET_PATH_VERSIONS_SUCCESS':
            return _getPathVersionsSuccess(state, action);

        // GET Path Version Steps (Collection)
        case 'GET_PATH_VERSION_STEPS_ERROR':
            return _getPathVersionStepsError(state);
        case 'GET_PATH_VERSION_STEPS_REQUEST':
            return _getPathVersionStepsRequest(state);
        case 'GET_PATH_VERSION_STEPS_SUCCESS':
            return _getPathVersionStepsSuccess(state, action);

        // GET Paths (Collection)
        case 'GET_PATHS_ERROR':
            return _getPathsError(state, action);
        case 'GET_PATHS_REQUEST':
            return _getPathsRequest(state, action);
        case 'GET_PATHS_SUCCESS':
            return _getPathsSuccess(state, action);

        // GET Step (Instance)
        case 'GET_STEP_ERROR':
            return _getStepError(state);
        case 'GET_STEP_REQUEST':
            return _getStepRequest(state);
        case 'GET_STEP_SUCCESS':
            return _getStepSuccess(state, action);

        // GET Step Inputs (Collection)
        case 'GET_STEP_INPUTS_ERROR':
            return _getStepInputsError(state);
        case 'GET_STEP_INPUTS_REQUEST':
            return _getStepInputsRequest(state);
        case 'GET_STEP_INPUTS_SUCCESS':
            return _getStepInputsSuccess(state, action);

        // GET Steps (Collection)
        case 'GET_STEPS_ERROR':
            return _getStepsError(state);
        case 'GET_STEPS_REQUEST':
            return _getStepsRequest(state);
        case 'GET_STEPS_SUCCESS':
            return _getStepsSuccess(state, action);

        // UPDATE Path (Instance)
        case 'UPDATE_PATH_ERROR':
            return _updatePathError(state);
        case 'UPDATE_PATH_REQUEST':
            return _updatePathRequest(state);
        case 'UPDATE_PATH_SUCCESS':
            return _updatePathSuccess(state, action);

        // UPDATE Path Source (Instance)
        case 'UPDATE_PATH_SOURCE_ERROR':
            return _updatePathSourceError(state);
        case 'UPDATE_PATH_SOURCE_REQUEST':
            return _updatePathSourceRequest(state);
        case 'UPDATE_PATH_SOURCE_SUCCESS':
            return _updatePathSourceSuccess(state, action);

        // UPDATE Path Output Reference
        case 'UPDATE_PATH_OUTPUT_REFERENCE_ERROR':
            return _updatePathOutputReferenceError(state);
        case 'UPDATE_PATH_OUTPUT_REFERENCE_REQUEST':
            return _updatePathOutputReferenceRequest(state);
        case 'UPDATE_PATH_OUTPUT_REFERENCE_SUCCESS':
            return _updatePathOutputReferenceSuccess(state, action);

        // UPDATE Step (Instance)
        case 'UPDATE_STEP_ERROR':
            return _updateStepError(state);
        case 'UPDATE_STEP_REQUEST':
            return _updateStepRequest(state);
        case 'UPDATE_STEP_SUCCESS':
            return _updateStepSuccess(state, action);

        // UPDATE Step Version (Instance)
        case 'UPDATE_STEP_VERSION_ERROR':
            return _updateStepVersionError(state);
        case 'UPDATE_STEP_VERSION_REQUEST':
            return _updateStepVersionRequest(state);
        case 'UPDATE_STEP_VERSION_SUCCESS':
            return _updateStepVersionSuccess(state, action);

        // UPDATE Step Selection
        case 'UPDATE_STEP_SELECTION':
            return _updateStepSelection(state, action);

        default:
            return state;
    }
};


//*************************************************
//*****   Global UI State   ***********************
//*************************************************
export function _setFlyoutData(state, payload, isOverride=false) {
    let newFlyoutMeta = {
        ...state.detail.flyoutMeta,
        ...payload
    };
    if (isOverride) {
        newFlyoutMeta = payload;
    }
    return {
        ...state,
        detail: {
            ...state.detail,
            flyoutMeta: newFlyoutMeta,
        }
    };
}



//*************************************************
//*****   Clear Path Data   ***********************
//*************************************************
function _clearPath() {
    return INITIAL_STATE;
};

function _clearStepInputs(state) {
    return {
        ...state,
        detail: {
            ...state.detail,
            pathInputs: [],
        }
    };
}



//*************************************************
//*******   COPY Path (Instance)   ****************
//*************************************************
function _copyPathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            copyPath: false,
        }
    };
};

function _copyPathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            copyPath: true,
        }
    };
};

function _copyPathSuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            copyPath: false,
        }
    };
};



//*************************************************
//*****   CREATE Path (Instance)   ****************
//*************************************************
function _createPathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPath: false,
        }
    };
};

function _createPathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPath: true,
        }
    };
};

function _createPathSuccess(state, payload) {
    const newList = [ ...state.list, payload ];

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPath: false,
        },
        list: newList
    };
};



//*************************************************
//*****   CREATE Path Publish (Instance)   ********
//*************************************************
function _createPathPublishError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathPublish: false,
        }
    };
};

function _createPathPublishRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathPublish: true,
        },
    };
};

function _createPathPublishSuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathPublish: false,
        },
    };
};



//*************************************************
//*****   CREATE Path Version Copy (Instance)   ***
//*************************************************
function _createPathVersionCopyError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionCopy: false,
        }
    };
}

function _createPathVersionCopyRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionCopy: true,
        },
    };
}

function _createPathVersionCopySuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionCopy: false,
        },
    };
}



//*************************************************
//***   CREATE Path Version Revert (Instance)   ***
//*************************************************
function _createPathVersionRevertError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionRevert: false,
        }
    };
}

function _createPathVersionRevertRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionRevert: true,
        },
    };
}

function _createPathVersionRevertSuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVersionRevert: false,
        },
    };
}



//*************************************************
//*****   CREATE Step (Instance)   ****************
//*************************************************
function _createStepError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStep: false,
        }
    };
};

function _createStepRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStep: true,
        }
    };
};

function _createStepSuccess(state, payload) {
    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    createStepsGraph(payload);

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    const stepsSequence = _getStepsSequence(payload, firstStep);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createStep: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
        },
    };
};



//*************************************************
//*****   CREATE Virtual Step (Instance)   ********
//*************************************************
function _createVirtualStep(state, payload) {
    const { stepType, toStepId, factoryName } = payload;
    const stepKey = stepType.toLowerCase();
    const newStepId = `virtual-step`;
    let newState = { ...state };

    const virtualStep = {
        id: newStepId,
        isComplete: false,
        name: translate(`common.step.${stepKey}.name`),
        description: translate(`common.step.${stepKey}.desc`),
        stepFactory: { name:factoryName, type:stepType },
        stepFactoryVersion: {
            outputSchema: {
                properties: {},
                required: [],
            },
            inputFields: [],
            stepFactory: { name:factoryName, type:stepType },
        },
        stepInputMappings: [],
        toSteps: [
            { stepId:toStepId },
        ],
    };

    const stateWithoutVirtual = _deleteVirtualStep(state);

    const newSequence = _sequenceAddStepBefore(stateWithoutVirtual.detail.sequence, newStepId, toStepId);

    newState = {
        ...stateWithoutVirtual,
        detail: {
            ...stateWithoutVirtual.detail,
            sequence: newSequence,
            steps: { ...stateWithoutVirtual.detail.steps, [newStepId]: virtualStep },
        }
    };

    // select virtual step
    return _getStepSuccess(newState, { state, payload:virtualStep });
};



//*************************************************
//*****   DELETE Path (Instance)   ****************
//*************************************************
function _deletePathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePath: false,
        }
    };
};

function _deletePathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePath: true,
        }
    };
};

function _deletePathSuccess(state, payload) {
    const newList = state.list.filter(path => path.id !== payload);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePath: false,
        },
        list: newList,
    };
};


//*************************************************
//*****   DELETE Step (Instance)   ****************
//*************************************************
function _deleteStepError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStep: false,
        }
    };
};

function _deleteStepRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStep: true,
        }
    };
};

function _deleteStepSuccess(state, payload) {
    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    createStepsGraph(payload);

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    const stepsSequence = _getStepsSequence(payload, firstStep);

    // update step selection
    let stepSelectionToUpdate = state.detail.stepSelection;
    if (stepsDictionary[stepSelectionToUpdate.id]) {
        stepSelectionToUpdate = stepsDictionary[stepSelectionToUpdate.id];
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteStep: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
            stepSelection: stepSelectionToUpdate,
        }
    };
};



//*************************************************
//*****   DELETE Virtual Step (Instance)   ********
//*************************************************
function _deleteVirtualStep(state) {
    const virtualStep = Object.values(state.detail.steps).find(x => x.id.indexOf('virtual-') > -1);
    let newState;

    if (virtualStep) {
        const { [virtualStep.id]:deletedStep, ...reducedStepsList } = state.detail.steps;

        newState = {
            ...state,
            detail: {
                ...state.detail,
                sequence: _deleteStepFromSequence(state.detail.sequence, deletedStep.id),
                steps: reducedStepsList,
            }
        };
    }

    return newState || state;
};



//*************************************************
//*****   Export Path (Instance)   ****************
//*************************************************
function _exportPathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportPath: false,
        }
    };
};

function _exportPathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportPath: true,
        }
    };
};

function _exportPathSuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportPath: false,
        }
    };
};

function _promotePathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            promotePath: true,
        }
    };
};

// Promote copies the published path into another draft server-side; the client holds no
// local state for the destination, so both success and error just clear the loading flag.
function _promotePathSettled(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            promotePath: false,
        }
    };
};



//*************************************************
//*****   Import Path (Instance)   ****************
//*************************************************
function _importPathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            importPath: false,
        }
    };
};

function _importPathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            importPath: true,
        }
    };
};

function _importPathSuccess(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            importPath: false,
        }
    };
};



//*************************************************
//*****   GET Path (Instance)   *******************
//*************************************************
function _getPathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPath: false,
        }
    };
};

function _getPathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPath: true,
        }
    };
};

function _getPathSuccess(state, action) {
    const { payload } = action;

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPath: false,
        },
        detail: {
            ...state.detail,
            path: payload,
        }
    };
};



//*************************************************
//*****   GET Path Usage (Instance)   *************
//*************************************************
function _getPathUsageError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathUsage: false,
        }
    };
};

function _getPathUsageRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathUsage: true,
        }
    };
};

function _getPathUsageSuccess(state, action) {
    const { payload } = action;

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathUsage: false,
        },
        usage: payload,
    };
};



//*************************************************
//*****   GET Path Versions (Instance)   **********
//*************************************************
function _getPathVersionsError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersions: false,
        }
    };
}

function _getPathVersionsRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersions: true,
        }
    };
}

function _getPathVersionsSuccess(state, action) {
    const { payload } = action;

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersions: false,
        },
        detail: {
            ...state.detail,
            pathVersions: payload,
        }
    };
}



//*************************************************
//*****   GET Path Version Steps (Collection)   ***
//*************************************************
function _getPathVersionStepsError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersionSteps: false,
        }
    };
}

function _getPathVersionStepsRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersionSteps: true,
        }
    };
}

function _getPathVersionStepsSuccess(state, action) {
    const { payload } = action;

    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    createStepsGraph(action.payload);

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    const stepsSequence = _getStepsSequence(payload, firstStep);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVersionSteps: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
        }
    };
}



//*************************************************
//*****   GET Paths (Collection)   ***************
//*************************************************
function _getPathsError(state, action) {
    const loadingPart = {
        getPaths: false
    };
    if (action.opts?.type) {
        loadingPart[`getPaths${capitalizeEachWord(action.opts?.type)}`] = false;
    }
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            ...loadingPart
        }
    };
};

function _getPathsRequest(state, action) {
    const loadingPart = {
        getPaths: true
    };
    if (action.opts?.type) {
        loadingPart[`getPaths${capitalizeEachWord(action.opts?.type)}`] = true;
    }
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            ...loadingPart
        }
    };
};

function _getPathsSuccess(state, action) {
    const loadingPart = {
        getPaths: false
    };
    if (action.opts?.type) {
        loadingPart[`getPaths${capitalizeEachWord(action.opts?.type)}`] = false;
    }
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            ...loadingPart
        },
        list: uniqBy([ ...state.list, ...action.payload ], 'id')
    };
};



//*************************************************
//*****   GET Step (Instance)   *******************
//*************************************************
function _getStepError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStep: false,
        }
    };
};

function _getStepRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStep: true,
        }
    };
};

function _getStepSuccess(state, action) {
    const { payload:step } = action;

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStep: false,
        },
        detail: {
            ...state.detail,
            stepSelection: _enrichStepSelection(state, step),
        }
    };
};



//*************************************************
//*****   GET Step Inputs (Collection)   **********
//*************************************************
function _getStepInputsError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepInputs: false,
        }
    };
};

function _getStepInputsRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepInputs: true,
        }
    };
};

function _getStepInputsSuccess(state, action) {
    const { payload } = action;

    // the availability map comes back in API order; present steps in execution order
    // (the path sequence), with any step missing from the sequence kept after them in API order
    const sequenceIndex = new Map(state.detail.sequence.flat(Infinity).map((stepId, index) => [ stepId, index ]));
    const stepIds = Object.keys(payload).sort((a, b) =>
        (sequenceIndex.get(a) ?? Infinity) - (sequenceIndex.get(b) ?? Infinity)
    );

    const result = stepIds.map(stepId => {
        return {
            isAvailable: payload[stepId],
            outputSchema: state.detail.steps[stepId]?.stepFactoryVersion?.outputSchema || null,
            stepId,
            stepName: state.detail.steps[stepId]?.name,
            stepOutput: state.detail.steps[stepId]?.stepOutput || null,
        };
    });

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getStepInputs: false,
        },
        detail: {
            ...state.detail,
            pathInputs: result,
        }
    };
};



//*************************************************
//*****   GET Steps (Collection)   ****************
//*************************************************
function _getStepsError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSteps: false,
        },
    };
};

function _getStepsRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSteps: true,
        },
    };
};

function _getStepsSuccess(state, action) {
    const { payload } = action;

    createStepsGraph(action.payload);

    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    const stepsSequence = _getStepsSequence(payload, firstStep);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSteps: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
        }
    };
};



//*************************************************
//*****   UPDATE Path (Instance)   ****************
//*************************************************
function _updatePathError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: false,
        }
    };
};

function _updatePathRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: true,
        }
    };
};

function _updatePathSuccess(state, action) {
    const { payload } = action;
    const newList = state.list.map(path => {
        if (path.id === payload.id) {
            return payload;
        }
        return path;
    });

    const isLoading = {
        ...state.isLoading,
        updatePath: false,
    };

    // saving another path (clearing the previous default, say) must not take over the open one
    const isOpenPath = !state.detail.path?.id || state.detail.path.id === payload.id;
    if (!isOpenPath) {
        return { ...state, isLoading, list: newList };
    }

    const currentSelectedStep = structuredClone(state.detail.stepSelection);
    const stepsCopy = structuredClone(state.detail.steps);
    const fakeInputs = _getFakeInputsBasedOnPath(state, currentSelectedStep, payload);
    // replace the input fields of the current selected step and the steps dictionary
    if (fakeInputs) {
        currentSelectedStep.stepFactoryVersion.inputFields = fakeInputs;
        stepsCopy[currentSelectedStep.id].stepFactoryVersion.inputFields = fakeInputs;
    }

    return {
        ...state,
        isLoading,
        detail: {
            ...state.detail,
            path: payload,
            stepSelection: currentSelectedStep,
            steps: stepsCopy,
        },
        list: newList,
    };
};



//*************************************************
//*****   UPDATE Path (Instance)   ****************
//*************************************************
function _updatePathSourceError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: false,
        }
    };
};

function _updatePathSourceRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: true,
        }
    };
};

function _updatePathSourceSuccess(state, action) {
    const { path, steps } = action.payload;
    const stateWithUpdatedPath = {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: false,
        },
        detail: {
            ...state.detail,
            path
        },
    };
    const stepsDictionary = getObjectFromArray(steps.map(item => (_enrichStepSelection(stateWithUpdatedPath, item))), 'id');
    const step = steps.find(item => item.id === stateWithUpdatedPath.detail.stepSelection.id);
    createStepsGraph(steps);

    return {
        ...stateWithUpdatedPath,
        detail: {
            ...stateWithUpdatedPath.detail,
            steps: stepsDictionary,
            stepSelection: _enrichStepSelection(stateWithUpdatedPath, step),
        },
    };
};



//*************************************************
//*****   UPDATE Path Output Reference   **********
//*************************************************
function _updatePathOutputReferenceError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: false,
        }
    };
};

function _updatePathOutputReferenceRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: true,
        }
    };
};

function _updatePathOutputReferenceSuccess(state, action) {
    const { payload } = action;
    const steps = payload;
    const stepsDictionary = getObjectFromArray(steps.map(item => (_enrichStepSelection(state, item))), 'id');
    const step = steps.find(item => item.id === state.detail.stepSelection.id);
    createStepsGraph(steps);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePath: false,
        },
        detail: {
            ...state.detail,
            steps: stepsDictionary,
            stepSelection: _enrichStepSelection(state, step),
        },
    };
};



//*************************************************
//*****   UPDATE Step (Instance)   ****************
//*************************************************
function _updateStepError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStep: false,
        }
    };
};

function _updateStepRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStep: true,
        }
    };
};

function _updateStepSuccess(state, action) {
    const { payload, stepId } = action;
    const step = payload.find(x => x.id === stepId) || {};

    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    createStepsGraph(payload);

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    let stepsSequence = _getStepsSequence(payload, firstStep);

    // preserve the virtual step
    if (state.detail.steps['virtual-step']) {
        const virtualStep = state.detail.steps['virtual-step'];
        stepsDictionary['virtual-step'] = virtualStep;
        stepsSequence = _sequenceAddStepBefore(stepsSequence, virtualStep.id, virtualStep.toSteps[0].stepId);
    }

    // update pathInputs - available-data API
    const updatedPathInputs = state.detail.pathInputs.map(pathInput => {
        if (pathInput.stepId === stepId) {
            return {
                ...pathInput,
                stepOutput: step.stepOutput
            };
        }
        return pathInput;
    });

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStep: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
            stepSelection: _enrichStepSelection(state, step),
            pathInputs: updatedPathInputs
        },
    };
};



//*************************************************
//*****   UPDATE Step Version (Instance)   ********
//*************************************************
function _updateStepVersionError(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepVersion: false,
        }
    };
};

function _updateStepVersionRequest(state) {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepVersion: true,
        }
    };
};

function _updateStepVersionSuccess(state, action) {
    const { payload, stepId } = action;
    const step = payload.find(x => x.id === stepId) || {};

    createStepsGraph(action.payload);

    // create steps dictionary
    const stepsDictionary = getObjectFromArray(payload.map(item => (_enrichStepSelection(state, item))), 'id');

    // create steps sequence
    const firstStep = payload.find(x => x.isRoot);
    const stepsSequence = _getStepsSequence(payload, firstStep);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateStepVersion: false,
        },
        detail: {
            ...state.detail,
            sequence: stepsSequence,
            steps: stepsDictionary,
            stepSelection: _enrichStepSelection(state, step),
        },
    };
};



//*************************************************
//*****   UPDATE Step Selection   *****************
//*************************************************
function _updateStepSelection(state, action) {
    const { stepId } = action;
    const step = Object.values(state.detail.steps).find(x => x.id === stepId);

    return {
        ...state,
        detail: {
            ...state.detail,
            stepSelection: _enrichStepSelection(state, step),
        }
    };
};



//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _deleteStepFromSequence
 * desc: deletes step from sequence list by id
 * note: can be used to delete a temporary virtual step
 * @param  {array}  sequence     Original sequence list
 * @param  {String} stepId       step id to delete
 * @return {String}              Updated sequence list
 */
function _deleteStepFromSequence(sequence, stepId) {
    const walkSequenceHelper = (arr, newArr) => {
        arr.forEach(item => {
            if (Array.isArray(item)) {
                newArr.push(Array.from(walkSequenceHelper(item, [], "")));
            }
            else {
                if (item !== stepId) {
                    newArr.push(item);
                }
            }
        });
        return newArr;
    };

    return walkSequenceHelper(sequence, [], '');
};


/**
 * _enrichStepSelection
 * desc: adds additional data to inputFields array
 * note: includes mapped input fields
 * @param  {object}  state  Store state
 * @param  {object}  step   Original step object
 * @return {object}         Enriched step object
 */
function _enrichStepSelection(state, step) {
    const stepCopy = structuredClone(step);
    if (stepCopy.stepFactoryVersion) {
        stepCopy.stepFactoryVersion.inputFields = stepCopy.stepFactoryVersion.inputFields.map(input => {
            const mappedInput = step.stepInputMappings.find(x => x.inputFieldId === input.id);
            return {
                ...input,
                mappedInput: mappedInput || null,
            };
        });

        const fakeInputs = _getFakeInputsBasedOnPath(state, stepCopy, state.detail.path);
        if (fakeInputs) stepCopy.stepFactoryVersion.inputFields = fakeInputs;
    }

    return stepCopy;
};


/**
 * _getFakeInputsBasedOnPath
 * desc: creates fake inputs based on step and path
 * note: since the source data for Junction path and intents data for Journey path belongs to "path" and not to the step, we need to mock that data in step inputs format.
 * @param  {object} state      state
 * @param  {object} step       Current step
 * @param  {object} path       Current path
 * @return {array || null}     Fake inputs list based on the path data
 */
function _getFakeInputsBasedOnPath(state, step, path) {
    //TODO: when the BE will move the data from path to step, this function need to be removed
    let result = null;
    const stepType = step && step.stepFactoryVersion ? step.stepFactoryVersion.stepFactory.type : null;
    
    // check for JUNCTION_SOURCE step
    if (stepType === 'JUNCTION_SOURCE') {
        // Env-typed junctions persist as `{ environmentFieldDefinitionId }` (no intentId).
        // Emit them as a data-expression token so InputIntents' combinedIntents — which
        // keys env rows by buildDataExpression('environment', envFieldDefId) — selects them.
        // Defensive against malformed junctions (no intentId and no env id) — skip them
        // rather than throwing here.
        const intentsList = path.pathVersion?.junctions
            ?.map((x) => {
                if (x.environmentFieldDefinitionId) return buildDataExpression('environment', x.environmentFieldDefinitionId);
                if (x.intentId) return `"${x.intentId}"`;
                return null;
            })
            .filter(Boolean);

        result = [
            {
                id: 'journey-junction-input-id',
                name: 'intents',
                description: translate('journeysDetail.flyout.defaultTemplate.intents.description'),
                label: translate('journeysDetail.flyout.defaultTemplate.intents.name'),
                type: 'RESOURCE',
                resourceType: 'JUNCTION_INTENT',
                subtype: null,
                isRequired: false,
                mappedInput: {
                    'inputFieldId': 'journey-junction-input-id',
                    'expression': intentsList?.length > 0 ? `[${intentsList.join(',')}]` : null
                }
            }
        ];
    }

    if (stepType === 'SOURCE') {
        let sources = path.pathVersion?.sourceSubscriptions?.map(x => x.sourceId).filter(Boolean);

        // An env DIRECTORY-backed subscription carries `environmentFieldDefinitionId` —
        // surface the env data-expression so the picker preselects the env row (mirrors
        // the env-source-manual handling below). Otherwise fall back to the raw sourceId.
        const firstSubscription = path.pathVersion?.sourceSubscriptions?.[0];
        let sourceExpression = null;
        if (firstSubscription?.environmentFieldDefinitionId) {
            sourceExpression = buildDataExpression('environment', firstSubscription.environmentFieldDefinitionId);
        } else if (sources?.length > 0) {
            sourceExpression = `"${sources[0]}"`;
        }

        const messageGetters = path.pathVersion?.sourceSubscriptions?.map((item, index) => {
            return {
                id: `junction-message-getter-${index + 1}-input-id`,
                name: 'message-getter',
                label: translate('journeysDetail.flyout.sourceTemplate.messageGetters.name', { number: index + 1 }),
                type: 'PLAIN_TEXT',
                subtype: null,
                isRequired: index === 0,
                mappedInput: {
                    'inputFieldId': `junction-message-getter-${index + 1}-input-id`,
                    'expression': item?.filter ? item.filter?.directoryName : ''
                }
            };
        });

        result = [ {
            id: 'junction-connection-input-id',
            name: 'sources',
            label: translate('journeysDetail.flyout.sourceTemplate.connection.name'),
            type: 'RESOURCE',
            resourceType: 'JUNCTION_SOURCE',
            subtype: null,
            isRequired: true,
            mappedInput: {
                'inputFieldId': 'junction-connection-input-id',
                'expression': sourceExpression
            }
        },
        ...messageGetters || []
        ];
    }

    // path-version data like junctions and subscriptions, so it joins the first step's fake inputs;
    // a fallback path cannot reference one, hence never on FALLBACK_SOURCE
    if (stepType === 'JUNCTION_SOURCE' || stepType === 'SOURCE') {
        result = [ ...(result || []), _getFallbackPathFakeInput(path) ];
    }

    if (step.stepFactoryVersion && step.stepFactoryVersion.sourceTypeId) {
        const sourceInput = {
            id: 'step-connection-input-id',
            name: 'step-connection',
            label: translate('journeysDetail.flyout.sourceTemplate.connection.name'),
            type: 'RESOURCE',
            resourceType: 'SOURCE_MANUAL',
            isRequired: true,
            mappedInput: {
                'inputFieldId': 'step-connection-input-id',
                // Env-source steps surface the env data-expression so consumers (e.g.
                // Definition's expressionToHtml) render a colored env Label automatically.
                'expression': step.environmentFieldDefinitionId
                    ? buildDataExpression('environment', step.environmentFieldDefinitionId)
                    : step.sourceId
            }
        };

        result = [ sourceInput, ...(result || []), ...step.stepFactoryVersion.inputFields.filter(item => item.resourceType !== 'SOURCE_MANUAL') ];
    }

    return result;
};


/**
 * _getFallbackPathFakeInput
 * desc: fake input holding the path version's fallback path reference
 * note: a direct reference is the quoted path id, an environment reference the env data-expression (the
 *       picker keys env rows by it), and no reference at all is null — the tenant default applies then
 * @param  {object} path  Current path
 * @return {object}       Fake input
 */
function _getFallbackPathFakeInput(path) {
    const fallbackPathId = path.pathVersion?.fallbackPathId;
    const environmentFieldDefinitionId = path.pathVersion?.fallbackPathEnvironmentFieldDefinitionId;
    let expression = null;
    if (environmentFieldDefinitionId) expression = buildDataExpression('environment', environmentFieldDefinitionId);
    else if (fallbackPathId) expression = `"${fallbackPathId}"`;

    const { ID, NAME } = journeysConstants.FALLBACK_PATH_INPUT;
    return {
        id: ID,
        name: NAME,
        description: translate('journeysDetail.flyout.defaultTemplate.fallbackPath.description'),
        label: translate('journeysDetail.flyout.defaultTemplate.fallbackPath.name'),
        type: 'RESOURCE',
        resourceType: 'FALLBACK_PATH',
        subtype: null,
        isRequired: false,
        mappedInput: {
            inputFieldId: ID,
            expression
        }
    };
}


/**
 * _getStepsSequence (recursive)
 * desc: creates matrix of stepIds for rendering workflow sequence
 * note: returns an multi-dimensional array of arrays=ULs and strings=LIs
 * @param  {array}  steps     Steps List
 * @param  {object} currStep  Current step (starting point)
 * @return {array}            Steps Sequence
 */
function _getStepsSequence(steps, currStep) {
    let sequence = [];

    // traverse toSteps to create sequence
    while (currStep) {
        const numToSteps = currStep?.toSteps?.length;
        const isBranchingStep = numToSteps > 1;
        const isDirectStep = numToSteps === 1;
        const isTerminationStep = numToSteps === 0 || journeysConstants.TERMINATION_STEPS.includes(currStep?.stepFactoryVersion?.stepFactory?.type);

        // add currStep
        sequence.push(currStep.id);

        // if termination step, end loop
        if (isTerminationStep) currStep = null;
        // if direct step, update currStep w toStep
        else if (isDirectStep) currStep = steps.find(x => x.id === currStep.toSteps[0].stepId);
        // if branch step, assemble branches (recursive)
        else if (isBranchingStep) {
            let branches = [];
            currStep.toSteps.forEach(toStep => {
                const firstStep = steps.find(x => x.id === toStep.stepId);
                const branch = _getStepsSequence(steps, firstStep);
                branches[toStep.order] = branch;
            });
            sequence.push(branches);
            currStep = null;
        }
    }

    return sequence;
};



/**
 * _sequenceAddStepBefore
 * desc: inserts step in sequence before specified step id
 * note: can be used to add a temporary virtual step
 * @param  {array}  sequence     Original sequence list
 * @param  {String} stepId       step id to insert
 * @param  {String} beforeStepId  step id to insert before
 * @return {String}              Updated sequence list
 */
function _sequenceAddStepBefore(sequence, stepId, beforeStepId) {
    const walkSequenceHelper = (arr, newArr) => {
        arr.forEach(item => {
            if (Array.isArray(item)) {
                newArr.push(Array.from(walkSequenceHelper(item, [], "")));
            }
            else {
                if (item === beforeStepId) {
                    newArr.push(stepId);
                }
                newArr.push(item);
            }
        });
        return newArr;
    };

    return walkSequenceHelper(sequence, [], '');
};


export default pathsReducer;
